import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button, CloseButton, Form, Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserTimes } from '@fortawesome/free-solid-svg-icons';
import IconAlert from 'components/common/IconAlert';
import useRequestState from 'hooks/useRequestState';
import {
  NETWORK_ERROR_MESSAGE,
  getApiErrorMessage,
  rejectRegistration
} from 'helpers/registrationApi';
import {
  InitialsAvatar,
  RolePill,
  ToneIcon
} from 'components/registration-shared/RegistrationUI';
import {
  getFullName,
  getRequestedRoleId
} from 'components/registration-shared/registrationFormat';

const QUICK_REASONS = [
  'Not a known employee',
  'Duplicate request',
  'Wrong agency',
  "Details couldn't be verified"
];

const MIN_REASON_LENGTH = 3;
const MAX_REASON_LENGTH = 500;

/**
 * Reject with a mandatory reason (U4.4 / D12): the button stays disabled until
 * a reason is written, because `rejection_reason` is the only record of why.
 */
const RejectRegistrationModal = ({ show, record, onHide, onRejected }) => {
  const [reason, setReason] = useState('');
  const [serverError, setServerError] = useState('');
  const submitReq = useRequestState();
  const submitting = submitReq.isLoading;
  const recordId = record?.id;
  const trimmed = reason.trim();
  const reasonValid = trimmed.length >= MIN_REASON_LENGTH;

  useEffect(() => {
    if (show) {
      setReason('');
      setServerError('');
    }
  }, [show, recordId]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!reasonValid || submitting || !record) {
      return;
    }
    setServerError('');

    const result = await submitReq.run(() =>
      rejectRegistration({ registrationId: record.id, reason: trimmed })
    );
    if (result.stale) {
      return;
    }
    if (!result.ok) {
      const message = getApiErrorMessage(result.error, NETWORK_ERROR_MESSAGE);
      setServerError(message);
      toast.error(message);
      return;
    }
    if (result.data.ok) {
      onRejected({ record, reason: trimmed, response: result.data });
      return;
    }
    const message = result.data.message || 'Unable to reject this request.';
    setServerError(message);
    toast.error(message);
  };

  return (
    <Modal
      show={show}
      onHide={submitting ? undefined : onHide}
      className="mym-queue-modal"
      backdrop={submitting ? 'static' : true}
      keyboard={!submitting}
      aria-labelledby="reject-registration-title"
    >
      <div className="mym-modal-head mym-tone-danger">
        <ToneIcon icon={faUserTimes} tone="danger" size="lg" solid />
        <div className="min-w-0">
          <h5 id="reject-registration-title" className="mym-modal-title">
            Reject request
          </h5>
          <p className="mym-modal-sub">This can't be undone.</p>
        </div>
        <CloseButton
          onClick={onHide}
          disabled={submitting}
          aria-label="Close"
        />
      </div>

      <Form noValidate onSubmit={handleSubmit}>
        <Modal.Body className="p-3 p-md-4">
          {record && (
            <div className="mym-applicant-summary mb-3">
              <InitialsAvatar record={record} />
              <div className="flex-1 min-w-0">
                <div className="fw-bold text-900 text-truncate">
                  {getFullName(record)}
                </div>
                <div className="fs-11 text-600 text-truncate">
                  {record.email}
                </div>
              </div>
              <RolePill
                roleId={getRequestedRoleId(record)}
                className="d-none d-sm-inline-flex"
              />
            </div>
          )}

          <Form.Group controlId="reject-reason">
            <Form.Label className="required">Reason</Form.Label>
            <div className="mym-quick-reasons mb-2" aria-label="Common reasons">
              {QUICK_REASONS.map(option => (
                <button
                  key={option}
                  type="button"
                  className={classNames('mym-quick-reason', {
                    'is-active': trimmed === option
                  })}
                  onClick={() => {
                    setReason(option);
                    setServerError('');
                  }}
                  disabled={submitting}
                >
                  {option}
                </button>
              ))}
            </div>
            <Form.Control
              as="textarea"
              rows={3}
              value={reason}
              maxLength={MAX_REASON_LENGTH}
              placeholder="Pick a reason above or write your own"
              onChange={e => {
                setReason(e.target.value);
                setServerError('');
              }}
              disabled={submitting}
              aria-describedby="reject-reason-help"
            />
            <div
              id="reject-reason-help"
              className="d-flex justify-content-between gap-3 mt-1 fs-11 text-600"
            >
              <span>
                Saved with the request. It may be shared with the applicant.
              </span>
              <span className="text-nowrap">
                {reason.length}/{MAX_REASON_LENGTH}
              </span>
            </div>
          </Form.Group>

          {serverError && (
            <IconAlert variant="danger" className="mt-3 mb-0">
              <p className="mb-0 fw-semibold">{serverError}</p>
            </IconAlert>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="falcon-default"
            onClick={onHide}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="danger"
            disabled={!reasonValid || submitting}
          >
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Rejecting…
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faUserTimes} className="me-2" />
                Reject request
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

RejectRegistrationModal.propTypes = {
  show: PropTypes.bool.isRequired,
  record: PropTypes.object,
  onHide: PropTypes.func.isRequired,
  onRejected: PropTypes.func.isRequired
};

export default RejectRegistrationModal;
